import { z } from "zod";

function parseBoolean(value?: string | boolean | null) {
  if (typeof value === "boolean") return value;
  if (!value) return false;
  return ["true", "1", "yes", "on"].includes(String(value).toLowerCase());
}

const rawEnv = {
  apiUrl: import.meta.env.VITE_API_URL,
  siteUrl: import.meta.env.VITE_SITE_URL,
  r2PublicBaseUrl: import.meta.env.VITE_R2_PUBLIC_BASE_URL,
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITEKEY,
  stripePublishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  stripeGrowthPriceId: import.meta.env.VITE_STRIPE_GROWTH_PRICE_ID,
  auth0Domain: import.meta.env.VITE_AUTH0_DOMAIN,
  auth0ClientId: import.meta.env.VITE_AUTH0_CLIENT_ID,
  auth0Audience: import.meta.env.VITE_AUTH0_AUDIENCE,
  posthogKey: import.meta.env.VITE_POSTHOG_KEY,
  posthogHost: import.meta.env.VITE_POSTHOG_HOST,
  monitorEndpoint: import.meta.env.VITE_MONITOR_ENDPOINT,
  monitorToken: import.meta.env.VITE_MONITOR_TOKEN,
  socialConnections: import.meta.env.VITE_AUTH0_SOCIAL_CONNECTIONS,
  sentryDsn: import.meta.env.VITE_SENTRY_DSN,
};

const envSchema = z.object({
  apiUrl: z.string().url(),
  siteUrl: z.string().url().optional(),
  r2PublicBaseUrl: z.string().url().optional(),
  turnstileSiteKey: z.string().optional(),
  stripePublishableKey: z.string().optional(),
  stripeGrowthPriceId: z.string().optional(),
  auth0Domain: z.string().optional(),
  auth0ClientId: z.string().optional(),
  auth0Audience: z.string().optional(),
  posthogKey: z.string().optional(),
  posthogHost: z.string().url().optional(),
  monitorEndpoint: z.string().url().optional(),
  monitorToken: z.string().optional(),
  socialConnections: z.string().optional(),
  sentryDsn: z.string().url().optional(),
});

const parsed = envSchema.safeParse(rawEnv);

function normalizeUrl(value?: string | null) {
  if (!value) return undefined;
  return value.endsWith("/") ? value.slice(0, -1) : value;
}

if (!parsed.success) {
  parsed.error.issues.forEach((issue) => {
    console.warn(`[env] ${issue.path.join(".")}: ${issue.message}`);
  });
}

export const env = {
  apiUrl: normalizeUrl(rawEnv.apiUrl) ?? "",
  siteUrl: normalizeUrl(rawEnv.siteUrl),
  r2PublicBaseUrl: normalizeUrl(rawEnv.r2PublicBaseUrl) ?? "https://cdn.lighthouse.jobs",
  turnstileSiteKey: rawEnv.turnstileSiteKey ?? "",
  stripePublishableKey: rawEnv.stripePublishableKey ?? "",
  stripeGrowthPriceId: rawEnv.stripeGrowthPriceId ?? "",
  auth0Domain: rawEnv.auth0Domain ?? "",
  auth0ClientId: rawEnv.auth0ClientId ?? "",
  auth0Audience: rawEnv.auth0Audience ?? "",
  posthogKey: rawEnv.posthogKey ?? "",
  posthogHost: normalizeUrl(rawEnv.posthogHost) ?? "https://app.posthog.com",
  monitorEndpoint: rawEnv.monitorEndpoint ?? undefined,
  monitorToken: rawEnv.monitorToken ?? "",
  socialConnections:
    rawEnv.socialConnections
      ?.split(',')
      .map((value: string) => value.trim())
      .filter(Boolean) ?? [],
  sentryDsn: rawEnv.sentryDsn,
};

export const criticalEnvSatisfied = Boolean(env.apiUrl);
